"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackWorks = [{
  id: "fw-1",
  ordinal: "01",
  title: "National SEO Growth",
  tags: ["National SEO", "Strategy"],
  client: "Retail Brand",
  year: "2024",
  featured: true,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp-wvc-sizes",
  link: "/CaseStudiesPage"
}, {
  id: "fw-2",
  ordinal: "02",
  title: "B2B Lead Generation",
  tags: ["B2B SEO", "Lead Gen"],
  client: "SaaS Firm",
  year: "2024",
  featured: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_gen.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_gen.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_gen.png-wvc-sizes",
  link: "/CaseStudiesPage"
}, {
  id: "fw-3",
  ordinal: "03",
  title: "Ecommerce Visibility",
  tags: ["Ecommerce", "Technical"],
  client: "Fashion Retail",
  year: "2023",
  featured: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_visibility.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_visibility.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_visibility.webp-wvc-sizes",
  link: "/CaseStudiesPage"
}, {
  id: "fw-4",
  ordinal: "04",
  title: "Local Search Dominance",
  tags: ["Local SEO", "Maps"],
  client: "Service Group",
  year: "2023",
  featured: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_search_dominance.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_search_dominance.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_search_dominance.png-wvc-sizes",
  link: "/CaseStudiesPage"
}, {
  id: "fw-5",
  ordinal: "05",
  title: "Technical SEO Audit",
  tags: ["Technical", "Audit"],
  client: "Tech Startup",
  year: "2022",
  featured: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png-wvc-sizes",
  link: "/CaseStudiesPage"
}];
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}
function WorksListConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const items = !loading && posts && posts.length > 0 ? posts.slice(0, 5).map((post, i) => {
    const categoryNames = (post.categories || []).map(c => c.name).filter(Boolean);
    const tags = categoryNames.length > 0 ? categoryNames.slice(0, 2) : ["SEO", "Strategy"];
    const year = post.date ? new Date(post.date).getFullYear().toString() : "";
    return {
      id: String(post.id),
      ordinal: String(i + 1).padStart(2, "0"),
      title: stripHtml(post.title || "Project Result"),
      tags,
      client: post.author?.name || "Client Partner",
      year,
      featured: i === 0,
      image: post.featuredImage || fallbackWorks[i % fallbackWorks.length].image,
      link: post.link || "/CaseStudiesPage",
      isDynamic: true
    };
  }) : fallbackWorks.map(w => ({
    ...w,
    isDynamic: false
  }));
  return <ul className="mt-16 md:mt-24 border-t border-[var(--border)]">
      {items.map((item, i) => <li data-index={i} key={item.id} className="group relative border-b border-[var(--border)] transition-colors duration-300 hover:bg-[var(--card)]">
          <Link to={item.link} className="grid grid-cols-12 gap-4 md:gap-6 items-start md:items-center py-8 md:py-10 px-2 md:px-4 min-h-[140px] md:min-h-[200px] text-[var(--foreground)] no-underline">
            <div className="col-span-2 md:col-span-1 flex items-start md:items-center">
              <span className="font-sans text-xs md:text-sm tracking-[0.14em] uppercase" style={{
            color: "var(--muted-foreground)"
          }}>
                {item.ordinal}
              </span>
            </div>

            <div className="col-span-10 md:col-span-5 relative">
              {item.featured && <span aria-hidden="true" className="hidden md:inline-flex absolute -left-16 top-1/2 -translate-y-1/2 items-center justify-center rounded-lg text-center leading-tight" style={{
            width: "64px",
            height: "64px",
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
            transform: "translateY(-50%) rotate(-10deg)",
            fontSize: "11.5px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 500
          }}>
                  {"Verified"}
                </span>}
              <h3 className="font-serif leading-[1.05] tracking-tight transition-transform duration-300 group-hover:translate-x-2" style={{
            fontSize: "clamp(1.5rem, 1.8vw, 1.95rem)",
            color: "var(--foreground)"
          }}>
                {item.title}
              </h3>
              {item.featured && <span className="md:hidden mt-3 inline-flex items-center rounded-lg px-3 py-1" style={{
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
            fontSize: "11.5px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 500
          }}>
                  {"Verified"}
                </span>}
            </div>

            <div className="col-span-7 md:col-span-3 flex flex-wrap items-center gap-2">
              {item.tags.map((tag, ti) => <span data-index={ti} key={ti} className="inline-flex items-center rounded-lg px-3 py-1 font-sans" style={{
            border: "1px solid var(--border)",
            color: "var(--foreground)",
            fontSize: "13.5px",
            letterSpacing: "0.02em"
          }}>
                  {tag}
                </span>)}
            </div>

            <div className="col-span-5 md:col-span-3 flex md:justify-end items-center gap-3">
              <div className="flex flex-col md:items-end">
                <span className="font-sans" style={{
              fontSize: "16px",
              color: "var(--muted-foreground)"
            }}>
                  {item.client}
                </span>
                {item.year && <span className="font-sans" style={{
              fontSize: "16px",
              color: "var(--muted-foreground)"
            }}>
                    {item.year}
                  </span>}
              </div>
              <span aria-hidden="true" className="hidden md:inline-flex items-center justify-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ml-2" style={{
            color: "var(--primary)"
          }}>
                <ArrowUpRight className="size-6" strokeWidth={1.25} />
              </span>
            </div>
          </Link>
        </li>)}
    </ul>;
}
export default function Impact() {
  const wp_query_params = {
    post_type: "post",
    per_page: 5,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="23060" id="localbusinessimpact" data-nav="light" className="bg-background text-foreground py-32 md:py-44" style={{
    paddingLeft: "clamp(20px, 5vw, 80px)",
    paddingRight: "clamp(20px, 5vw, 80px)"
  }}>
      <div className="mx-auto max-w-[1440px]">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-3">
            <div className="w-full mb-6" style={{
            height: "1px",
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />
            <p className="font-sans uppercase mb-4" style={{
            fontSize: "13.5px",
            letterSpacing: "0.14em",
            color: "var(--muted-foreground)",
            fontWeight: 500
          }}>
              {"Case Studies"}
            </p>
            <p className="font-sans" style={{
            fontSize: "16px",
            lineHeight: 1.55,
            color: "var(--muted-foreground)"
          }}>
              {"Real results for ambitious businesses. See my methodology."}
            </p>
          </div>

          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif leading-[0.95] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.65rem)",
            color: "var(--foreground)",
            fontWeight: 400
          }}>
              {"Results that "}
              <span className="relative inline-block italic" style={{
              color: "var(--foreground)"
            }}>
                {"matter"}
                <svg aria-hidden="true" viewBox="0 0 200 20" preserveAspectRatio="none" className="absolute left-0 right-0 -bottom-2 md:-bottom-3 w-full" style={{
                height: "clamp(10px, 1.6vw, 18px)"
              }}>
                  <path d="M2 14 C 30 6, 70 18, 110 10 S 180 6, 198 12" fill="none" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" style={{
                  opacity: 0.9
                }} />
                </svg>
              </span>
              {" most."}
            </h2>
            <div className="mt-10 md:mt-12">
              <Link to="/CaseStudiesPage">
                <Button variant="outline" className="rounded-lg border-[1.5px] px-8 py-6 h-auto font-sans" style={{
                borderColor: "var(--foreground)",
                color: "var(--foreground)",
                backgroundColor: "transparent",
                fontSize: "17px",
                letterSpacing: "0.01em"
              }}>
                  {"View all case studies"}
                  <ArrowUpRight className="size-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 5,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <WorksListConsumer />
        </PostsProvider>
      </div>
    </section>;
}